/**
 * 在途监管平台 — 登录页（独立页，不位于任何角色 Shell 内）
 * 三角色选择：政府监管 / 运输企业 / 运输司机 → 登录后跳转对应角色首页
 * N6(2026-09-04)：司机角色走 POST /driver/auth/login（transit_drivers 档案独立登录，
 * 返回标准 JWT）；企业/政府角色仍走 /auth/login 用户体系。
 *
 * ★ 2026-09-12 视觉重构：采用「左侧品牌 Hero + 右侧白卡」双栏结构，
 *   配色沿用云牛集品牌深青（tokens.css），图标全部改为手写 inline SVG（无 icon 库依赖）。
 *   行为契约（角色分支、请求路径/字段、token 键名、跳转规则、错误信息透出）保持不变。
 */
import { useEffect, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { driverLogin, login } from '@/api/auth';
import { ApiError, TOKEN_KEY, USER_KEY } from '@/api/http';
import type { User } from '@/types';
import { defaultHome } from '@/utils/roleHome';
import '@/styles/login.css';

type RoleKey = 'gov' | 'enterprise' | 'driver';

interface RoleOption {
  key: RoleKey;
  label: string;
  icon: ReactElement;
}

/** 记住我 —— 仅持久化账号（绝不保存密码） */
const REMEMBER_KEY = 'yunniuji_transport_remember';

const ICON_GOV: ReactElement = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3.2 19 6v6.1c0 4.1-2.9 7.5-7 8.7-4.1-1.2-7-4.6-7-8.7V6l7-2.8Z" />
    <path d="m9.2 12.2 2 2 3.6-3.8" />
  </svg>
);

const ICON_ENTERPRISE: ReactElement = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.6 6.5h10.2v9.2H2.6z" />
    <path d="M12.8 9.8h3.9l3.7 3.4v2.5h-7.6z" />
    <circle cx="6.4" cy="18.4" r="1.7" />
    <circle cx="17" cy="18.4" r="1.7" />
  </svg>
);

const ICON_DRIVER: ReactElement = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="8.6" />
    <circle cx="12" cy="12" r="2.9" />
    <path d="M12 3.4v3M12 20.6v-3M3.4 12h3M20.6 12h-3" />
  </svg>
);

const ICON_USER: ReactElement = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8.2" r="3.6" />
    <path d="M4.8 19.8c0-3.6 3.2-5.9 7.2-5.9s7.2 2.3 7.2 5.9" />
  </svg>
);

const ICON_LOCK: ReactElement = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4.6" y="10.4" width="14.8" height="9.4" rx="2.2" />
    <path d="M8.2 10.4V8a3.8 3.8 0 0 1 7.6 0v2.4" />
    <path d="M12 14v2.4" />
  </svg>
);

const ICON_EYE: ReactElement = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.6 12S6.2 5.9 12 5.9 21.4 12 21.4 12 17.8 18.1 12 18.1 2.6 12 2.6 12Z" />
    <circle cx="12" cy="12" r="2.9" />
  </svg>
);

const ICON_EYE_OFF: ReactElement = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.4 3.4 20.6 20.6" />
    <path d="M10.3 6.2A10.4 10.4 0 0 1 12 6c5.8 0 9.4 6 9.4 6a17 17 0 0 1-2.7 3.4" />
    <path d="M6.5 8.4A16.4 16.4 0 0 0 2.6 12S6.2 18.1 12 18.1c1.3 0 2.5-.2 3.6-.6" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

const ICON_ALERT: ReactElement = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="8.6" />
    <path d="M12 7.6v5.2" />
    <path d="M12 16.4h.01" />
  </svg>
);

const ICON_SPINNER: ReactElement = (
  <svg className="login-spinner" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.6" fill="none" stroke="rgba(255,255,255,0.32)" strokeWidth="2.6" />
    <path d="M12 3.4a8.6 8.6 0 0 1 8.6 8.6" fill="none" stroke="#ffffff" strokeWidth="2.6" strokeLinecap="round" />
  </svg>
);

const ROLE_OPTIONS: RoleOption[] = [
  { key: 'gov', label: '政府监管', icon: ICON_GOV },
  { key: 'enterprise', label: '运输企业', icon: ICON_ENTERPRISE },
  { key: 'driver', label: '运输司机', icon: ICON_DRIVER },
];

const ROLE_LABEL: Record<RoleKey, string> = {
  gov: '政府监管',
  enterprise: '运输企业',
  driver: '运输司机',
};

const HERO_CHIPS: string[] = ['GPS 轨迹追踪', '电子围栏告警', '检疫证照核验', '区块链存证', '三方协同'];

/** 按角色给出登录说明（司机端为独立档案登录，口径必须与其他端区分） */
function tipForRole(role: RoleKey): string {
  if (role === 'driver') {
    return '司机端使用司机备案手机号 + 独立密码登录，密码由所属运输企业在「司机管理」中设置。';
  }
  return '使用在途监管平台已注册账号登录，账号可填写用户名或备案手机号。';
}

/** 登录失败文案映射：HTTP 403=账号锁定，429=限流，其余原样透出后端 message */
function messageForError(e: unknown): string {
  if (e instanceof ApiError) {
    const status: number = e.status ?? 0;
    if (status === 403 || e.code === 403) return '账号已锁定，请联系运输企业管理员';
    if (status === 429 || e.code === 429) return '操作过于频繁，请稍后再试';
    return e.message || '登录失败，请稍后重试';
  }
  if (e instanceof Error && e.message) return e.message;
  return '登录失败，请稍后重试';
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  const [role, setRole] = useState<RoleKey>('gov');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // 记住我：挂载时回填账号（仅账号，不回填密码）
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) {
      setUsername(saved);
      setRemember(true);
    }
  }, []);

  // 已登录访问 /login → 跳回 redirect 或角色默认首页
  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) {
      navigate(redirect ?? defaultHome(), { replace: true });
    }
  }, [navigate, redirect]);

  const persistPrincipal = (principal: User) => {
    localStorage.setItem(USER_KEY, JSON.stringify(principal));
  };

  /** 切换角色时清空旧错误，避免残留提示误导用户 */
  const changeRole = (next: RoleKey) => {
    setRole(next);
    setError('');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    if (!username.trim() || !password.trim()) {
      setError('请输入账号（或手机号）和密码');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      if (role === 'driver') {
        // 司机：transit_drivers 档案独立登录（手机号 + 司机密码）
        const result = await driverLogin({ phone: username.trim(), password });
        localStorage.setItem(TOKEN_KEY, result.token);
        persistPrincipal({
          id: result.driver_id,
          username: result.phone,
          real_name: result.name,
          phone: result.phone,
          roles: ['driver'],
          identity: 'driver',
        });
      } else {
        const result = await login({ username: username.trim(), password });
        localStorage.setItem(TOKEN_KEY, result.access_token);
        persistPrincipal(result.user);
      }
      // 记住我：只落账号，任何情况下都不写密码
      if (remember) {
        localStorage.setItem(REMEMBER_KEY, username.trim());
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      const target = redirect ?? (role === 'driver' ? '/driver' : `/${role}`);
      navigate(target, { replace: true });
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        {/* ===== 左：品牌 Hero（≤980px 隐藏） ===== */}
        <section className="login-hero">
          <div className="login-hero-brand">
            <span className="login-hero-logo">
              <img src="/logo.svg" alt="云牛集" width={40} height={40} />
            </span>
            <span className="login-hero-brand-text">
              <span className="login-hero-brand-name">云牛集</span>
              <span className="login-hero-brand-sub">牛只在途监管平台</span>
            </span>
          </div>

          <h1 className="login-hero-title">
            牛只在途监管
            <span className="login-hero-title-accent">全程可溯</span>
          </h1>

          <p className="login-hero-desc">
            贯通运输订单、GPS 轨迹、电子围栏、检疫核验与区块链存证全链路，
            服务政府监管、运输企业与司机三方协同，让每一头牛的在途状态实时可见、可查、可证。
          </p>

          <ul className="login-hero-chips">
            {HERO_CHIPS.map((chip) => (
              <li key={chip} className="login-chip">
                {chip}
              </li>
            ))}
          </ul>
        </section>

        {/* ===== 右：登录白卡 ===== */}
        <section className="login-pane">
          <div className="login-panel">
            <header className="login-panel-head">
              <span className="login-eyebrow">统一身份认证</span>
              <h2 className="login-panel-title">账号登录</h2>
              <p className="login-panel-sub">请选择登录身份，进入对应的业务工作台</p>
            </header>

            <div className="login-roles" role="tablist" aria-label="选择登录角色">
              {ROLE_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="tab"
                  aria-selected={role === opt.key}
                  className="login-role"
                  onClick={() => changeRole(opt.key)}
                >
                  <span className="login-role-icon">{opt.icon}</span>
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>

            {error ? (
              <div className="login-alert" role="alert">
                <span className="login-alert-icon">{ICON_ALERT}</span>
                <span>{error}</span>
              </div>
            ) : null}

            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <div className="login-field">
                <label className="login-label" htmlFor="login-username">
                  {role === 'driver' ? '司机手机号' : '账号 / 手机号'}
                </label>
                <div className="login-input">
                  <span className="login-input-icon">{ICON_USER}</span>
                  <input
                    id="login-username"
                    type="text"
                    value={username}
                    onChange={(evt) => setUsername(evt.target.value)}
                    placeholder={role === 'driver' ? '请输入司机备案手机号' : '请输入账号或手机号'}
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-label" htmlFor="login-password">
                  密码
                </label>
                <div className="login-input">
                  <span className="login-input-icon">{ICON_LOCK}</span>
                  <input
                    id="login-password"
                    type={reveal ? 'text' : 'password'}
                    value={password}
                    onChange={(evt) => setPassword(evt.target.value)}
                    placeholder={role === 'driver' ? '请输入司机登录密码' : '请输入登录密码'}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-reveal"
                    onClick={() => setReveal((prev) => !prev)}
                    aria-label={reveal ? '隐藏密码' : '显示密码'}
                    aria-pressed={reveal}
                    title={reveal ? '隐藏密码' : '显示密码'}
                  >
                    {reveal ? ICON_EYE_OFF : ICON_EYE}
                  </button>
                </div>
              </div>

              <div className="login-extra">
                <label className="login-remember">
                  <input
                    type="checkbox"
                    className="login-checkbox"
                    checked={remember}
                    onChange={(evt) => setRemember(evt.target.checked)}
                  />
                  <span>记住账号</span>
                </label>
                <span className="login-aux">忘记密码请联系平台管理员</span>
              </div>

              <button type="submit" className="login-submit" disabled={submitting}>
                {submitting ? ICON_SPINNER : null}
                <span>{submitting ? `登录中…` : `登录${ROLE_LABEL[role]}端`}</span>
              </button>
            </form>

            <p className="login-hint">{tipForRole(role)}</p>
          </div>

          <p className="login-footer">© 2026 云牛集 · 牛只在途监管平台</p>
        </section>
      </div>
    </div>
  );
}
